import { useState, useEffect } from "react";
import styles from "../onboard.module.css";
import { Row, Card, Col } from "react-bootstrap";
import FileUploader from "../studentDetails/fileUpload";
import CommonButton from "../../CommonButton/CommonButton";
import { documentUploadApi } from "../../../api";
import { useMyContext } from "../../../myContext";
import greentickmark from "../../signup/assets/greenTickIcon.svg";

const DocumentTypeUpload = ({ reqDocs, stepToShow, setStepsToShow }) => {
  const { remitterUserId } = useMyContext("");
  const acceptedFormats = ".jpeg, .jpg, .png .pdf";
  const [uploadError, setUploadError] = useState("");

  // State for file management
  const [loading, setLoading] = useState(false);
  const [panFile, setPanFile] = useState(null);
  const [panFileType, setPanFileType] = useState(null);

  const [aadharFrontFile, setAadharFront] = useState(null);
  const [aadharBackFile, setAadharBack] = useState(null);
  const [aadharFrontFileType, setAadharFrontFileType] = useState(null);
  const [aadharBackFileType, setAadharBackFileType] = useState(null);
  const [passportFrontFile, setPassportFront] = useState(null);
  const [passportBackFile, setPassportBack] = useState(null);
  const [passportFrontFileType, setPassportFrontFileType] = useState(null);
  const [passportBackFileType, setPassportBackFileType] = useState(null);

  // Upload states based on required documents
  const [uploadPAN, setUploadPAN] = useState(false);
  const [uploadAadhaar, setUploadAadhaar] = useState(false);
  const [uploadPassport, setUploadPassport] = useState(false);

  // this we have to add in theme or local storage
  const [isDocumentUploaded, setDocumentUploaded] = useState(false);

  // File validation function
  const checkValidFile = (files) => {
    return files.some(
      (file) =>
        acceptedFormats.includes(file.type) ||
        acceptedFormats.includes(`.${file.name.split(".").pop()}`),
    );
  };

  // PAN file drop handler
  const handlePanDrop = (files) => {
    if (checkValidFile(files)) {
      setUploadError("");

      setPanFile(files[0]);
      const fileExtension = files[0].name.split(".").pop();
      setPanFileType(fileExtension);
    }
  };

  // Aadhaar front file drop handler
  const handleFrontAadharDrop = (files) => {
    if (checkValidFile(files)) {
      setUploadError("");
      setAadharFront(files[0]);
      const fileExtension = files[0].name.split(".").pop();
      setAadharFrontFileType(fileExtension);
    }
  };

  // Aadhaar back file drop handler
  const handleBackAadharDrop = (files) => {
    if (checkValidFile(files)) {
      setUploadError("");
      setAadharBack(files[0]);
      const fileExtension = files[0].name.split(".").pop();
      setAadharBackFileType(fileExtension);
    }
  };

  // Passport front file drop handler
  const handleFrontPassportDrop = (files) => {
    if (checkValidFile(files)) {
      setUploadError("");
      setPassportFront(files[0]);
      const fileExtension = files[0].name.split(".").pop();
      setPassportFrontFileType(fileExtension);
    }
  };

  // Passport back file drop handler
  const handleBackPassportDrop = (files) => {
    if (checkValidFile(files)) {
      setUploadError("");
      setPassportBack(files[0]);
      const fileExtension = files[0].name.split(".").pop();
      setPassportBackFileType(fileExtension);
    }
  };

  // Remove button handler
  const onRemoveButton = (label) => {
    if (label === "PAN Card") {
      setPanFile(null);
      setPanFileType("");
    } else if (label === "Front of Aadhaar") {
      setAadharFront(null);
      setAadharFrontFileType("");
    } else if (label === "Back of Aadhaar") {
      setAadharBack(null);
      setAadharBackFileType("");
    } else if (label === "Front of Passport") {
      setPassportFront(null);
      setPassportFrontFileType("");
    } else if (label === "Back of Passport") {
      setPassportBack(null);
      setPassportBackFileType("");
    }
  };

  // Upload file function
  const uploadFile = async (file, fileType, docType, side) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await documentUploadApi(
        remitterUserId,
        fileType,
        docType,
        side,
        formData,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  // API functions for each document type
  const panApi = () => uploadFile(panFile, panFileType, "PAN", "front");
  const frontAadharApi = () =>
    uploadFile(aadharFrontFile, aadharFrontFileType, "AADHAAR", "front");
  const backAadharApi = () =>
    uploadFile(aadharBackFile, aadharBackFileType, "AADHAAR", "back");
  const frontPassportApi = () =>
    uploadFile(passportFrontFile, passportFrontFileType, "PASSPORT", "front");
  const backPassportApi = () =>
    uploadFile(passportBackFile, passportBackFileType, "PASSPORT", "back");

  // Validation function to check if all required files are uploaded
  const isValidForSubmission = () => {
    // Check PAN if required
    if (uploadPAN && !panFile) return false;

    // Check Aadhaar if required (both front and back needed)
    if (uploadAadhaar && (!aadharFrontFile || !aadharBackFile)) return false;

    // Check Passport if required (both front and back needed)
    if (uploadPassport && (!passportFrontFile || !passportBackFile))
      return false;

    // If no documents are required, return false (at least one document should be required)
    if (!uploadPAN && !uploadAadhaar && !uploadPassport) return false;

    return true;
  };

  // Submit function
  const checkIfValid = async () => {
    if (isValidForSubmission()) {
      try {
        setUploadError("");
        setLoading(true);
        const uploadPromises = [];

        if (uploadPAN && panFile) {
          uploadPromises.push(panApi());
        }
        if (uploadAadhaar && aadharFrontFile && aadharBackFile) {
          uploadPromises.push(frontAadharApi());
          uploadPromises.push(backAadharApi());
        }
        if (uploadPassport && passportFrontFile && passportBackFile) {
          uploadPromises.push(frontPassportApi());
          uploadPromises.push(backPassportApi());
        }

        await Promise.all(uploadPromises)
          .then((res) => {
            // Check for errors in the responses
            const errors = [];
            res.forEach((element) => {
              if (element.status === 400) {
                errors.push(
                  element.data.message || "An error occurred during upload",
                );
              }
            });
            if (errors.length > 0) {
              const errorMessage = errors.join("\n");
              setUploadError(errorMessage);
              setLoading(false);
              return;
            }

            setLoading(false);
            setDocumentUploaded(true);
            setStepsToShow("RemitterStep3");
          })
          .catch((error) => {
            setLoading(false);
            setUploadError(
              error?.message ||
                error?.toString() ||
                "Something went wrong, please try again later.",
            );
          });
      } catch (error) {
        setLoading(false);
        setUploadError(
          error?.message ||
            error?.toString() ||
            "Something went wrong, please try again later.",
        );
      }
    }
  };

  // Set upload states based on required documents
  useEffect(() => {
    setUploadPAN(reqDocs.includes("PAN"));
    setUploadAadhaar(reqDocs.includes("Aadhaar"));
    setUploadPassport(reqDocs.includes("Passport"));
  }, [reqDocs]);

  return (
    <Card className={styles.cardAccount}>
      <Card.Body className={styles.cardBodyAccount}>
        <Row className={styles.Title}>Document Upload</Row>
        {isDocumentUploaded && (
          <>
            <Row>
              <Col md={11} sm={11} xs={10} className={styles.details}>
                Successfully Uploaded!
              </Col>
              <Col md={1} sm={1} xs={2} style={{ position: "relative" }}>
                <img className={styles.greentick} src={greentickmark} alt="" />
              </Col>
            </Row>
          </>
        )}
        {stepToShow === "RemitterStep2" && (
          <div>
            <hr style={{ marginTop: "0px" }} />
            <div style={{ marginTop: "20px" }}>
              <Row style={{ marginBottom: "8px", justifyContent: "center" }}>
                {/* PAN Upload */}
                {uploadPAN && (
                  <Col
                    md={12}
                    sm={12}
                    xs={12}
                    lg={4}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      margin: "10px 0px",
                    }}
                  >
                    <FileUploader
                      onDrop={handlePanDrop}
                      acceptedFormats={acceptedFormats}
                      maxSize={5000000}
                      label="PAN Card"
                      loading={loading}
                      onRemoveButton={() => onRemoveButton("PAN Card")}
                    />
                  </Col>
                )}

                {/* Aadhaar Front Upload */}
                {uploadAadhaar && (
                  <Col
                    md={12}
                    sm={12}
                    xs={12}
                    lg={4}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      margin: "10px 0px",
                    }}
                  >
                    <FileUploader
                      onDrop={handleFrontAadharDrop}
                      acceptedFormats={acceptedFormats}
                      maxSize={5000000}
                      label="Front of Aadhaar"
                      loading={loading}
                      onRemoveButton={() => onRemoveButton("Front of Aadhaar")}
                    />
                  </Col>
                )}

                {/* Aadhaar Back Upload */}
                {uploadAadhaar && (
                  <Col
                    md={12}
                    sm={12}
                    xs={12}
                    lg={4}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      margin: "10px 0px",
                    }}
                  >
                    <FileUploader
                      onDrop={handleBackAadharDrop}
                      acceptedFormats={acceptedFormats}
                      maxSize={5000000}
                      label="Back of Aadhaar"
                      loading={loading}
                      onRemoveButton={() => onRemoveButton("Back of Aadhaar")}
                    />
                  </Col>
                )}

                {/* Passport Front Upload */}
                {uploadPassport && (
                  <Col
                    md={12}
                    sm={12}
                    xs={12}
                    lg={4}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      margin: "10px 0px",
                    }}
                  >
                    <FileUploader
                      onDrop={handleFrontPassportDrop}
                      acceptedFormats={acceptedFormats}
                      maxSize={5000000}
                      label="Front of Passport"
                      loading={loading}
                      onRemoveButton={() => onRemoveButton("Front of Passport")}
                    />
                  </Col>
                )}

                {/* Passport Back Upload */}
                {uploadPassport && (
                  <Col
                    md={12}
                    sm={12}
                    xs={12}
                    lg={4}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      margin: "10px 0px",
                    }}
                  >
                    <FileUploader
                      onDrop={handleBackPassportDrop}
                      acceptedFormats={acceptedFormats}
                      maxSize={5000000}
                      label="Back of Passport"
                      loading={loading}
                      onRemoveButton={() => onRemoveButton("Back of Passport")}
                    />
                  </Col>
                )}
              </Row>
            </div>

            <Row className={styles.allowedFormat}>
              Allowed Formats are jpeg, png, jpg, pdf and the size should be
              less then 5MB
            </Row>

            <Row className="pt-3 mt-3">
              {uploadError !== "" && (
                <div className={`${styles.errorState} ${styles.uploaderror}`}>
                  {uploadError}
                </div>
              )}
              <Col md={3}>
                <CommonButton
                  title="Next"
                  onClick={checkIfValid}
                  loading={loading}
                  disabled={false}
                  width={"150px"}
                />
              </Col>
            </Row>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default DocumentTypeUpload;
